import { useEffect } from 'react';
import { createFileRoute, useNavigate, useParams } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod/v3';
import { Loader2, TriangleAlert } from 'lucide-react';
import { trpc } from '@/lib/trpc';
import PageLoading from '@/components/PageLoading';

const evidenceSearchSchema = z.object({
  docId: z.string().optional(),
  submissionId: z.string().optional(),
});

export const Route = createFileRoute(
  '/(protected)/workspaces/$workspaceId/evidence/$evidenceId',
)({
  component: EvidenceRedirectPage,
  validateSearch: evidenceSearchSchema,
});

function EvidenceRedirectPage() {
  const navigate = useNavigate();
  const { evidenceId, workspaceId } = useParams({ strict: false }) as {
    evidenceId: string;
    workspaceId: string;
  };

  const executionQuery = useQuery({
    ...trpc.workflow.getExecutionForEvidence.queryOptions({ evidenceId }),
    enabled: !!evidenceId,
  });

  useEffect(() => {
    const executionId = executionQuery.data?.executionId;
    if (!executionId) return;

    navigate({
      to: '/workspaces/$workspaceId/compliance/runs/$executionId',
      params: { workspaceId, executionId },
      replace: true,
    });
  }, [executionQuery.data?.executionId, navigate, workspaceId]);

  if (executionQuery.isLoading) {
    return (
      <PageLoading />
    );
  }

  if (executionQuery.error || !executionQuery.data?.executionId) {
    return (
      <div className='flex h-screen w-full flex-col items-center justify-center gap-2 text-sm text-red-500'>
        <TriangleAlert className='h-5 w-5' />
        Unable to find a matching execution: {executionQuery.error?.message || 'No execution found'}
      </div>
    );
  }

  // Redirect will replace this view; keep it empty to avoid flicker
  return null;
}
